import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ExternalLink, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Reveal } from "@/components/Reveal";
import { supabase } from "@/integrations/supabase/client";
import { useLangPath } from "@/i18n/LangLayout";

interface MediaMention { id: string; title: string; outlet: string; url: string; published_date: string }

export const MediaSection = () => {
  const { t, i18n } = useTranslation();
  const lp = useLangPath();
  const [items, setItems] = useState<MediaMention[]>([]);

  useEffect(() => {
    supabase.from("media_mentions").select("id,title,outlet,url,published_date").eq("published", true).order("published_date", { ascending: false }).limit(3)
      .then(({ data }) => setItems(data || []));
  }, []);

  if (items.length === 0) return null;
  const localeMap: Record<string, string> = { he: "he-IL", ar: "ar", en: "en-US" };
  const dateLocale = localeMap[i18n.language] || "he-IL";

  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-secondary/30">
      <div className="container">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <div className="text-accent font-semibold text-sm tracking-wider uppercase mb-3">{t("media.label")}</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">{t("media.title")}</h2>
            <p className="text-muted-foreground text-lg">{t("media.homeSub")}</p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto mb-8 sm:mb-10">
          {items.map((m, i) => (
            <Reveal key={m.id} delay={i * 100}>
              <a href={m.url} target="_blank" rel="noopener noreferrer" className="block h-full">
                <Card className="group h-full p-6 hover:shadow-elegant transition-smooth border hover:border-accent/50 hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent bg-accent-soft px-3 py-1 rounded-full">
                      <Newspaper className="w-3 h-3" />
                      {m.outlet}
                    </span>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-smooth" />
                  </div>
                  <h3 className="font-heading text-base font-bold mb-3 leading-snug group-hover:text-accent transition-smooth">{m.title}</h3>
                  <div className="text-xs text-muted-foreground">
                    {new Date(m.published_date).toLocaleDateString(dateLocale, { year: "numeric", month: "long", day: "numeric" })}
                  </div>
                </Card>
              </a>
            </Reveal>
          ))}
        </div>

        <div className="text-center">
          <Link to={lp("/media")}>
            <Button variant="outline" size="lg">
              {t("media.viewAll")}
              <ArrowLeft className="w-4 h-4 ms-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
